import { useState, useCallback } from 'react';

export interface ParkerScaleInputProps {
  value: number;
  onChange: (value: number) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
  error?: string;
  required?: boolean;
}

const PARKER_SCALE_CATEGORIES = [
  { min: 96, max: 100, label: 'Extraordinary',               color: 'bg-secondary-700' },
  { min: 90, max: 95,  label: 'Outstanding',                 color: 'bg-primary-700' },
  { min: 80, max: 89,  label: 'Above Average to Very Good',  color: 'bg-gray-700' },
  { min: 70, max: 79,  label: 'Average',                     color: 'bg-gray-500' },
  { min: 60, max: 69,  label: 'Below Average',               color: 'bg-amber-500' },
  { min: 50, max: 59,  label: 'Unacceptable',                color: 'bg-error' },
] as const;

const getScoreCategory = (score: number) => {
  return PARKER_SCALE_CATEGORIES.find((cat) => score >= cat.min && score <= cat.max);
};

export const ParkerScaleInput = ({
  value,
  onChange,
  label = 'Wine Score',
  disabled = false,
  className = '',
  error,
  required = false,
}: ParkerScaleInputProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [inputValue, setInputValue] = useState(value.toString());

  const category = getScoreCategory(value);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      const numValue = parseInt(newValue, 10);
      if (!isNaN(numValue) && numValue >= 50 && numValue <= 100) {
        onChange(numValue);
      }
    },
    [onChange]
  );

  const handleBlur = useCallback(() => {
    // Reset to valid value on blur if invalid
    const numValue = parseInt(inputValue, 10);
    if (isNaN(numValue) || numValue < 50 || numValue > 100) {
      setInputValue(value.toString());
    }
  }, [inputValue, value]);

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseInt(e.target.value, 10);
      onChange(newValue);
      setInputValue(newValue.toString());
    },
    [onChange]
  );

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <button
          type="button"
          className="text-gray-400 hover:text-gray-600 focus:outline-none"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
          aria-label="Parker scale information"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </button>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute z-10 w-72 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg -mt-1 ml-4">
          <h4 className="font-semibold mb-2">Parker Scale Categories</h4>
          <ul className="space-y-1">
            {PARKER_SCALE_CATEGORIES.map((cat) => (
              <li key={cat.min} className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${cat.color}`} />
                <span>
                  {cat.min}-{cat.max}: {cat.label}
                </span>
              </li>
            ))}
          </ul>
          <div className="absolute -left-2 top-4 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-gray-900" />
        </div>
      )}

      <div className="flex items-center gap-4">
        {/* Number Input */}
        <div className="relative">
          <input
            type="number"
            min={50}
            max={100}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            disabled={disabled}
            className={`
              w-20 px-3 py-2 text-center text-lg font-semibold
              border rounded-lg
              focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500
              ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50'}
              ${error ? 'border-red-500' : 'border-gray-300'}
            `}
            aria-label="Wine score (50-100)"
          />
        </div>

        {/* Slider */}
        <div className="flex-1">
          <input
            type="range"
            min={50}
            max={100}
            value={value}
            onChange={handleSliderChange}
            disabled={disabled}
            className={`
              w-full h-2 rounded-lg appearance-none cursor-pointer
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              ${category?.color.replace('bg-', 'accent-') ?? 'accent-gray-400'}
            `}
            style={{
              background: `linear-gradient(to right, 
                #dc2626 0%, 
                #f97316 20%, 
                #eab308 40%, 
                #22c55e 60%, 
                #2563eb 80%, 
                #9333ea 100%
              )`,
            }}
            aria-label="Wine score slider"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>50</span>
            <span>100</span>
          </div>
        </div>
      </div>

      {/* Category Display */}
      {category && (
        <div className="mt-2 flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${category.color}`} />
          <span className="text-sm font-medium text-gray-700">{category.label}</span>
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
